import { useState } from "react";
import {
  createFileRoute,
  redirect,
  useSearch,
  Link,
} from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleSignInButton } from "@/components/auth/google-signin-button";
import { signUp } from "@/lib/auth-client";
import { Eye, EyeOff, Loader2, Check, X, ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/signup")({
  beforeLoad: async ({ context }) => {
    const session = context.session;
    if (session) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: SignupPage,
  validateSearch: (search: Record<string, unknown>): { error?: string } => ({
    error: search.error as string | undefined,
  }),
});

const ERROR_MESSAGES: Record<string, string> = {
  access_denied: "Sign-up was cancelled. Please try again.",
  server_error: "Unable to sign up. Please try again later.",
  invalid_request: "Configuration error. Please contact support.",
  USER_ALREADY_EXISTS: "An account with this email already exists.",
};

const passwordRequirements = [
  { test: (p: string) => p.length >= 8, label: "At least 8 characters" },
  {
    test: (p: string) => /[A-Z]/.test(p),
    label: "At least one uppercase letter",
  },
  {
    test: (p: string) => /[a-z]/.test(p),
    label: "At least one lowercase letter",
  },
  { test: (p: string) => /[0-9]/.test(p), label: "At least one number" },
];

function SignupPage() {
  const search = useSearch({ from: "/signup" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords don't match");
      setIsLoading(false);
      return;
    }

    const passwordValid = passwordRequirements.every((req) =>
      req.test(password),
    );
    if (!passwordValid) {
      setError("Password doesn't meet all requirements");
      setIsLoading(false);
      return;
    }

    try {
      const name = email.split("@")[0];
      const { error: signUpError } = await signUp.email({
        email,
        password,
        name,
        callbackURL: "/dashboard",
      });

      if (signUpError) {
        setError(signUpError.code || "Failed to create account");
      } else {
        window.location.href = `/verify-pending?email=${encodeURIComponent(email)}`;
      }
    } catch {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const allRequirementsMet = passwordRequirements.every((req) =>
    req.test(password),
  );
  const passwordsMatch =
    password === confirmPassword && confirmPassword.length > 0;

  const displayError = search.error
    ? ERROR_MESSAGES[search.error] || "An error occurred during sign up."
    : error
      ? ERROR_MESSAGES[error] || "An error occurred."
      : null;

  return (
    <div className="flex min-h-screen bg-background cinematic-noise">
      {/* Left Pane - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-16 xl:px-24 py-16 sm:py-12 relative z-10 overflow-y-auto">
        <div
          className="max-w-md w-full mx-auto space-y-8 sm:space-y-10 animate-fade-in mt-6 sm:mt-12 lg:mt-0"
          style={{ animationDelay: "100ms", animationFillMode: "both" }}
        >
          <div className="space-y-2 sm:space-y-3">
            <h1 className="text-4xl sm:text-5xl font-serif tracking-tight">
              Create an account.
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground font-light">
              Start creating in seconds.
            </p>
          </div>

          {displayError && (
            <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-4 animate-fade-in">
              <p className="text-sm text-destructive font-medium">
                {displayError}
              </p>
            </div>
          )}

          <div className="space-y-6">
            <GoogleSignInButton callbackURL="/dashboard" />

            <div className="relative flex items-center py-2">
              <div className="flex-grow border-t border-border"></div>
              <span className="flex-shrink-0 mx-4 text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground">
                Or register with email
              </span>
              <div className="flex-grow border-t border-border"></div>
            </div>

            <form onSubmit={handleEmailSignUp} className="space-y-5">
              <div className="space-y-2 group">
                <Label
                  htmlFor="email"
                  className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground group-focus-within:text-foreground transition-colors"
                >
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="email"
                  className="h-10 sm:h-12 bg-transparent border-x-0 border-t-0 border-b-2 border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary transition-all text-sm sm:text-base"
                />
              </div>

              <div className="space-y-2 group">
                <Label
                  htmlFor="password"
                  className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground group-focus-within:text-foreground transition-colors"
                >
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    autoComplete="new-password"
                    className="h-10 sm:h-12 bg-transparent border-x-0 border-t-0 border-b-2 border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary transition-all pr-10 text-sm sm:text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-2"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="size-4" />
                    ) : (
                      <Eye className="size-4" />
                    )}
                  </button>
                </div>

                {password.length > 0 && (
                  <div className="space-y-1.5 pt-2 text-[10px] sm:text-xs">
                    {passwordRequirements.map((req, index) => (
                      <div
                        key={index}
                        className={`flex items-center gap-2 transition-colors ${
                          req.test(password)
                            ? "text-primary"
                            : "text-muted-foreground"
                        }`}
                      >
                        {req.test(password) ? (
                          <Check className="size-3.5" />
                        ) : (
                          <div className="size-3.5 rounded-full border border-current opacity-50" />
                        )}
                        {req.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2 group">
                <Label
                  htmlFor="confirmPassword"
                  className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground group-focus-within:text-foreground transition-colors"
                >
                  Confirm Password
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                  className="h-10 sm:h-12 bg-transparent border-x-0 border-t-0 border-b-2 border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary transition-all text-sm sm:text-base"
                />
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="text-[10px] sm:text-xs text-destructive mt-1">
                    Passwords don't match
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-full text-base font-medium mt-6 sm:mt-8"
                disabled={isLoading || !allRequirementsMet || !passwordsMatch}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  "Create account"
                )}
              </Button>
            </form>
          </div>

          <p className="text-center text-xs sm:text-sm text-muted-foreground pb-8 lg:pb-0">
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-medium text-foreground hover:underline underline-offset-4 transition-all"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right Pane - Visual */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden bg-muted">
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent z-10" />
        <img
          src="/cdn-cgi/image/f=webp,w=1024/landing/sale.png"
          alt="Aesthetic product photography"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-10000 hover:scale-105 ease-out"
        />
        <div
          className="absolute bottom-12 left-12 right-12 z-20 animate-fade-in"
          style={{ animationDelay: "300ms", animationFillMode: "both" }}
        >
          <p className="text-2xl font-serif text-white/90 leading-snug">
            Generate, edit, and manage — all in one place. No design skills
            needed.
          </p>
        </div>
      </div>
    </div>
  );
}
